import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Context from '../../../App/MainProvider/context';
import MiciCaseContext from '../MiciCaseContext';
import Button from '../../../../shared/components/Button';
import SpinnerArea from '../../../../shared/components/SpinnerArea';
import miciCasesService from '../../../../services/miciCasesService';
import Table, { Cell } from '../../../../shared/components/Table';
import { MiciCaseAttachment, MiciCaseDocument } from '../../../../services/types/MiciCases';
import { dateStr } from '../../../../shared/misc/utils';
import { Modal } from '../../../../shared/components';
import Role from '../../../../types/role';
import Library from '../../../../shared/components/Library';

const MiciCaseDocuments: React.FC = () => {
  const { t } = useTranslation();
  const { service, inSuperUserMode, user } = useContext(Context);
  const {
    data: { miciCase },
    reload,
  } = useContext(MiciCaseContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [documentIdSelected, setDocumentIdSelected] = useState<number | null>(null);

  const download = (docId: number) => {
    service(() => miciCasesService.downloadAttachment(miciCase.id, docId), {
      loader: setIsSubmitting,
    });
  };

  const historyCells: Cell<MiciCaseDocument>[] = [
    {
      name: 'createdBy',
      value: (x) => x.createdBy,
    },
    {
      name: 'createdOn',
      column: 'date',
      value: (x) => dateStr(x.createdOn),
    },
    {
      name: 'stage',
      value: (x) => x.stage,
      column: 'miciStage',
    },
    {
      name: 'comment',
      value: (x) => x.comment,
      column: 'comments',
    },
    {
      column: 'attachments',
      // css: 'text-right',
      render: (_, obj) => (
        <>
          {obj.attachments.length > 0 && (
            <Button
              className="btn-tertiary no-padding"
              icon="file"
              text=""
              onClick={() => {
                setDocumentIdSelected(obj.id);
                setShowAttachmentsModal(true);
              }}
            />
          )}
        </>
      ),
    },
  ];

  const attachmentCells: Cell<MiciCaseAttachment>[] = [
    {
      name: 'documentName',
      value: (x) => x.documentName,
    },
    {
      column: 'download',
      css: 'text-right',
      render: (_, obj) => (
        <Button
          className="btn-tertiary no-padding"
          icon="file-download"
          text=""
          onClick={() => download(obj.id)}
        />
      ),
    },
  ];

  const canSave =
    miciCase.operation.isMember ||
    user.roleCode === Role.HSO ||
    user.roleCode === Role.GroupHead ||
    inSuperUserMode();

  return (
    <SpinnerArea loading={isSubmitting}>
      {canSave && (
        <Library
          id={miciCase.id}
          reload={reload}
          docService={miciCasesService}
          savedComment={miciCase.documentDraft ? miciCase.documentDraft.comment : undefined}
          addButtonBelow
        />
      )}

      <h5>History</h5>
      <Table data={miciCase.documents} cells={historyCells} keyFunc={(x) => x.id} emptyNoDesc />
      <Modal
        visible={showAttachmentsModal}
        setVisible={() => {
          setShowAttachmentsModal(false);
        }}
        title={t('attachments')}
        actions={[{ text: t('close'), action: () => {} }]}
        customClass="wide-modal"
      >
        <div className="history-content flow-history table-responsive">
          {documentIdSelected && (
            <Table
              data={miciCase.documents.filter((x) => x.id === documentIdSelected)[0].attachments}
              cells={attachmentCells}
              keyFunc={(x) => x.id}
              emptyNoDesc
            />
          )}
        </div>
      </Modal>
    </SpinnerArea>
  );
};

export default MiciCaseDocuments;
